/**
 * Enterprise WooCommerce Integration Service
 * Marple & Company - African Holistic Skincare
 */

import { Product } from '@/data/products';

// WooCommerce API Configuration
const API_BASE_URL = import.meta.env.VITE_WOOCOMMERCE_URL || 'https://marplecompany.co.za/wp-json/wc/v3';
const WOOCOMMERCE_KEY = import.meta.env.VITE_WOOCOMMERCE_KEY || '';
const WOOCOMMERCE_SECRET = import.meta.env.VITE_WOOCOMMERCE_SECRET || '';
const MARPLE_API = `${API_BASE_URL.replace('/wp/wp-json/wc/v3', '')}/wp/wp-json/marple/v1`;

// Build WooCommerce API URL with authentication
function buildWooUrl(endpoint: string): string {
  const url = new URL(`${API_BASE_URL}${endpoint}`);
  url.searchParams.append('consumer_key', WOOCOMMERCE_KEY);
  url.searchParams.append('consumer_secret', WOOCOMMERCE_SECRET);
  return url.toString();
}

// Types
export interface WooProduct {
  id: number;
  name: string;
  slug: string;
  permalink: string;
  price: string;
  regular_price: string;
  sale_price: string;
  on_sale: boolean;
  stock_status: 'instock' | 'outofstock' | 'onbackorder';
  categories: Array<{ id: number; name: string; slug: string }>;
  images: Array<{ id: number; src: string; alt: string }>;
  description: string;
  short_description: string;
  average_rating: string;
  rating_count: number;
}

export interface CartItem {
  /** Numeric WooCommerce product id, when the catalogue is WooCommerce-backed. */
  product_id?: number;
  /**
   * Local catalogue ids are slugs ('aqua-gel-candle'), not numbers. They are sent
   * as the SKU so the order still identifies a real product — the previous
   * `parseInt(slug)` produced NaN, which serialised to null on every line item.
   */
  sku?: string;
  name?: string;
  price?: number;
  quantity: number;
}

export interface ShippingAddress {
  first_name: string;
  last_name: string;
  company?: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

// API Helper Functions
async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Coupon API
export const WooCommerceCoupons = {
  async validate(code: string): Promise<{ valid: boolean; discount?: string; discount_type?: string; message?: string }> {
    if (!WOOCOMMERCE_KEY || !WOOCOMMERCE_SECRET) {
      return { valid: false, message: 'WooCommerce not configured' };
    }
    
    try {
      const url = buildWooUrl(`/coupons?code=${encodeURIComponent(code)}`);
      const response = await fetchAPI<{ id: number; amount: string; discount_type: string }[]>(url);
      
      if (response.length > 0) {
        const coupon = response[0];
        return {
          valid: true,
          discount: coupon.amount,
          discount_type: coupon.discount_type,
          message: `Coupon applied: ${coupon.discount_type === 'percent' ? coupon.amount + '%' : 'R' + coupon.amount}`
        };
      }
      return { valid: false, message: 'Invalid coupon code' };
    } catch (error) {
      console.error('Coupon validation error:', error);
      return { valid: false, message: 'Unable to validate coupon' };
    }
  }
};

// Product API
export const WooCommerceProducts = {
  // Get all products from WooCommerce
  async getAll(params?: {
    per_page?: number;
    category?: string;
    tag?: string;
    search?: string;
    orderby?: 'popularity' | 'date' | 'rating' | 'price';
    order?: 'asc' | 'desc';
  }): Promise<WooProduct[]> {
    if (!WOOCOMMERCE_KEY || !WOOCOMMERCE_SECRET) {
      console.log('WooCommerce not configured, using local data');
      return [];
    }

    const queryParams = new URLSearchParams();
    if (params?.per_page) queryParams.set('per_page', params.per_page.toString());
    if (params?.category) queryParams.set('category', params.category);
    if (params?.tag) queryParams.set('tag', params.tag);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.orderby) queryParams.set('orderby', params.orderby);
    if (params?.order) queryParams.set('order', params.order);

    try {
      const url = buildWooUrl(`/products?${queryParams}`);
      return await fetchAPI<WooProduct[]>(url);
    } catch (error) {
      console.log('WooCommerce fetch failed, using local data:', error);
      return [];
    }
  },

  // Get popular products by sales
  async getPopular(per_page: number = 3): Promise<WooProduct[]> {
    return WooCommerceProducts.getAll({ per_page, orderby: 'popularity', order: 'desc' });
  },

  // Get featured products
  async getFeatured(per_page: number = 10): Promise<WooProduct[]> {
    if (!WOOCOMMERCE_KEY || !WOOCOMMERCE_SECRET) return [];
    
    try {
      const url = buildWooUrl(`/products?featured=true&per_page=${per_page}`);
      return await fetchAPI<WooProduct[]>(url);
    } catch {
      return [];
    }
  },

  // Get single product
  async getById(id: number): Promise<WooProduct | null> {
    if (!WOOCOMMERCE_KEY || !WOOCOMMERCE_SECRET) return null;
    
    try {
      const url = buildWooUrl(`/products/${id}`);
      return await fetchAPI<WooProduct>(url);
    } catch {
      return null;
    }
  }
};

// Category API
export const WooCommerceCategories = {
  async getAll(): Promise<Array<{ id: number; name: string; slug: string }>> {
    if (!WOOCOMMERCE_KEY || !WOOCOMMERCE_SECRET) return [];
    
    try {
      const url = buildWooUrl('/products/categories?per_page=100');
      return await fetchAPI(url);
    } catch {
      return [];
    }
  }
};

// Order API
export const WooCommerceOrders = {
  async create(orderData: {
    payment_method: string;
    payment_method_title: string;
    set_paid: boolean;
    billing: ShippingAddress & { email: string; phone: string };
    shipping: ShippingAddress;
    line_items: CartItem[];
    shipping_lines?: Array<{ method_id: string; method_title: string; total: string }>;
    customer_note?: string;
  }): Promise<{ id: number; status: string; total: string; payment_url?: string; order_key?: string }> {
    if (!WOOCOMMERCE_KEY || !WOOCOMMERCE_SECRET) {
      throw new Error('WooCommerce not configured');
    }
    
    const url = buildWooUrl('/orders');
    return fetchAPI(url, {
      method: 'POST',
      body: JSON.stringify(orderData)
    });
  }
};

// Map WooCommerce category slugs to local category types
const WOO_CATEGORY_MAP: Record<string, Product['category']> = {
  candles: 'candles',
  'gel-candles': 'candles',
  tea: 'tea',
  'herbal-tea': 'tea',
  'herbal-teas': 'tea',
  'body-care': 'body-care',
  bodycare: 'body-care',
  skincare: 'skincare',
  'skin-care': 'skincare',
};

// Convert WooProduct to local Product format
export function convertWooProductToLocal(wooProduct: WooProduct): Product {
  const price = parseFloat(wooProduct.price) || 0;
  const originalPrice = wooProduct.regular_price && wooProduct.regular_price !== wooProduct.price
    ? parseFloat(wooProduct.regular_price)
    : undefined;

  const wooSlug = wooProduct.categories[0]?.slug || 'skincare';
  const category: Product['category'] = WOO_CATEGORY_MAP[wooSlug] || 'skincare';

  const cleanHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();
  const description = cleanHtml(wooProduct.short_description) || cleanHtml(wooProduct.description);

  return {
    id: wooProduct.id.toString(),
    name: wooProduct.name,
    category,
    description,
    price,
    originalPrice,
    size: '',
    keyIngredients: [],
    benefits: [],
    rating: parseFloat(wooProduct.average_rating) || 4.5,
    reviews: wooProduct.rating_count,
    inStock: wooProduct.stock_status === 'instock',
    stockCount: 0,
    images: wooProduct.images.length > 0
      ? wooProduct.images.map(img => img.src)
      : ['/products/placeholder.jpg'],
    tags: wooProduct.categories.map(c => c.slug),
    isBestseller: false,
    isNew: false,
  };
}

// Database integration for order persistence
export const OrderDatabase = {
  async saveOrder(orderData: {
    order_id: number;
    customer_email: string;
    total: number;
    items: CartItem[];
    status: string;
    created_at: string;
  }): Promise<void> {
    const API_URL = import.meta.env.VITE_API_URL || '/api';
    
    await fetch(`${API_URL}/v1/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    });
  },

  async getCustomerOrders(email: string): Promise<any[]> {
    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${API_URL}/v1/orders?email=${encodeURIComponent(email)}`);
    return response.json();
  },

  async createPayFlexSession(orderId: number, amount: number): Promise<{ redirect_url: string }> {
    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${API_URL}/v1/orders/payflex/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id: orderId,
        amount,
        currency: 'ZAR',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create PayFlex session');
    }

    return response.json();
  }
};

export default {
  products: WooCommerceProducts,
  categories: WooCommerceCategories,
  orders: WooCommerceOrders,
  database: OrderDatabase,
  convertWooProductToLocal
};
